import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import gameService from '../../services/gameService';

const initialState = {
  games: [],
  selectedGame: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
  createLoading: false,
  deleteLoading: {},
  statusUpdateLoading: {},
};

// Create game
export const createGame = createAsyncThunk(
  'game/createGame',
  async (gameData, { rejectWithValue }) => {
    try {
      const response = await gameService.createGame(gameData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create game');
    }
  }
);

// Get all games
export const fetchGames = createAsyncThunk(
  'game/fetchGames',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await gameService.getGames(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch games');
    }
  }
);

// Get game by ID
export const fetchGameById = createAsyncThunk(
  'game/fetchGameById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await gameService.getGameById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch game');
    }
  }
);

// Update game status
export const updateGameStatus = createAsyncThunk(
  'game/updateGameStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await gameService.updateGameStatus(id, status);
      return { id, status, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update game status');
    }
  }
);

// Delete game
export const deleteGame = createAsyncThunk(
  'game/deleteGame',
  async (id, { rejectWithValue }) => {
    try {
      await gameService.deleteGame(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete game');
    }
  }
);

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedGame: (state) => {
      state.selectedGame = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create game
      .addCase(createGame.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createGame.fulfilled, (state, action) => {
        state.createLoading = false;
        // Add new game to the beginning of the list
        if (action.payload.game) {
          state.games = [action.payload.game, ...state.games];
          state.pagination.totalCount += 1;
        }
      })
      .addCase(createGame.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })

      // Fetch games
      .addCase(fetchGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        state.loading = false;
        state.games = action.payload.games || [];
        state.pagination = {
          currentPage: action.payload.pagination?.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || 1,
          totalCount: action.payload.pagination?.totalCount || 0,
          limit: action.payload.pagination?.limit || 10,
          hasMore: action.payload.pagination?.hasMore || false,
        };
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch game by ID
      .addCase(fetchGameById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGame = action.payload.game || action.payload;
      })
      .addCase(fetchGameById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update game status
      .addCase(updateGameStatus.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.statusUpdateLoading[id] = true;
        state.error = null;
      })
      .addCase(updateGameStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.statusUpdateLoading[id] = false;

        // Update game in the list
        const gameIndex = state.games.findIndex(game => game._id === id);
        if (gameIndex !== -1) {
          state.games[gameIndex].status = status;
        }

        // Update selected game if it's the same
        if (state.selectedGame && state.selectedGame._id === id) {
          state.selectedGame.status = status;
        }
      })
      .addCase(updateGameStatus.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.statusUpdateLoading[id] = false;
        state.error = action.payload;
      })

      // Delete game
      .addCase(deleteGame.pending, (state, action) => {
        const id = action.meta.arg;
        state.deleteLoading[id] = true;
        state.error = null;
      })
      .addCase(deleteGame.fulfilled, (state, action) => {
        const id = action.payload;
        state.deleteLoading[id] = false;

        // Remove game from the list
        state.games = state.games.filter(game => game._id !== id);
        state.pagination.totalCount -= 1;

        // Clear selected game if it was deleted
        if (state.selectedGame && state.selectedGame._id === id) {
          state.selectedGame = null;
        }
      })
      .addCase(deleteGame.rejected, (state, action) => {
        const id = action.meta.arg;
        state.deleteLoading[id] = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearSelectedGame, clearError } = gameSlice.actions;

export default gameSlice.reducer;
